import { Fragment, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';

import services, {
  useCheckoutQuoteMutation,
  useGetCustomerAddressesQuery,
  useGetCustomerDetailsQuery,
  useGetListCustomerGroupsQuery,
  useGetShoppingCartDetailsQuery,
} from 'core/services';

import useIsLoading from 'hooks/useIsLoading.hook';
import useMessage from 'hooks/useMessage.hook';
import withProps from 'hooks/withProps';
import { getArray, getFunc } from 'utils/helpers';

import Tooltip from 'components/Tooltip';
import Button2 from 'components/Button2';

const {
  markOrderAsQuote,
  updateOrderShoppingCartId,
  updateOrderShippingStepData,
  updateOrderBillingStepData,
  clearOrder,
} = services;

const useStyles = makeStyles(() => ({
  form: {
    display: 'none',
  },
}));

// NOTE: Unkomment to emulate Greenwing login

// const greenwing_test = {
//   return_url: 'https://labscoop.gwpunchout.com/cXMLTransform.php?portalSetupID=1088',
//   auth_key: 'askdfjsfdu90283092uf90d',
//   buyer_cookie: 'askdfjsf09w8ef0w8efs0df8',
// };
const greenwing_test = false;

/**
 * Shows checkout button or Greenwing Transfer Cart button depending on different conditions.
 */
const CheckoutButton = (props) => {
  const {
    quote,
    label,
    onClick,
    showCount,
    disabled = false,
    size = 'medium',
    fullWidth = true,
    ButtonComponent = Button2,
    ...restButtonProps
  } = props;

  const m = useMessage();
  const classes = useStyles();
  const formRef = useRef(null);
  const history = useHistory();
  const cart_id = useSelector((state) => state.cart.cart_id);

  const meQuery = useGetCustomerDetailsQuery();
  const labGroupsQuery = useGetListCustomerGroupsQuery();
  const [checkoutQuote, quoteQuery] = useCheckoutQuoteMutation();
  const queryAddress = useGetCustomerAddressesQuery(undefined, {
    skip: !quote,
  });
  const cartDetailsQuery = useGetShoppingCartDetailsQuery(
    { cart_id },
    {
      skip: !cart_id,
    },
  );
  const cartDetails = cartDetailsQuery.data;
  const labGroupsList = getArray(labGroupsQuery.data);
  const me = meQuery.data;
  const addressList = getArray(queryAddress.data);

  const loading = useIsLoading(
    meQuery,
    labGroupsList,
    cartDetailsQuery,
    quoteQuery,
  );

  const isGroupOwner = useMemo(() => {
    const currentGroup = getArray(labGroupsList).find((lg) => {
      return lg.id === cartDetails?.lab_group?.id;
    });
    return currentGroup?.role === 'OWNER';
  }, [labGroupsList, cartDetails]);

  const isLabAdminOnly = cartDetails?.lab_group?.orders_placed_only_admin;
  const groupAdminApproveNeeded = isLabAdminOnly && !isGroupOwner;
  const greenwing = me?.greenwing || (!loading && greenwing_test);

  const allApproved = useMemo(() => {
    return getArray(cartDetails?.items).every(
      (item) => item.status === 'APPROVED',
    );
  }, [cartDetails]);

  const noItems = cartDetails?.cart_total_items === 0;

  // Checkout button label
  const buttonLabel = useMemo(() => {
    if (label) {
      // Custom label used
      return label;
    }
    // Show Transfer Cart instead of Checkout if greenwing login used
    const result = greenwing ? 'Transfer Cart' : 'Checkout';

    if (showCount) {
      // Show products count next to the label
      return `${result} (${cartDetails?.cart_total_items || 0})`;
    }
    return result;
  }, [cartDetails?.cart_total_items, greenwing, label, showCount]);

  const buttonDisabled = useMemo(() => {
    if (disabled) return true;

    if (quote) {
      return quote.status !== 'CONFIRMED' || quote.is_used;
    }
    return groupAdminApproveNeeded || !allApproved || noItems;
  }, [disabled, quote, groupAdminApproveNeeded, allApproved, noItems]);

  // Generate greenwing products with relevant schema
  const greenwingItems = useMemo(() => {
    if (!greenwing) {
      return null;
    }
    if (getArray(quote?.products).length > 0) {
      // For quote products
      return quote.products.map((item) => ({
        partnumber: item.sku,
        description: item.title,
        quantity: item.qty,
        unitprice: (item.new_price / 100).toFixed(2),
        unspsc: item.unspsc || '41000000',
        uom: item.unit_price,
      }));
    }
    // For usual products
    return getArray(cartDetails?.items).map((item) => ({
      partnumber: item.product.sku,
      description: item.product.title,
      quantity: item.qty,
      unitprice: (item.product.price / 100).toFixed(2),
      // TODO: Remove when this field will be available in product schema
      unspsc: item.product.unspsc || '41000000',
      uom: item.product.unit_price,
    }));
  }, [cartDetails, greenwing, quote]);

  const handleClick = useCallback(
    async (...args) => {
      getFunc(onClick)(...args);

      if (greenwing) {
        // Greenwing checkout
        const form = formRef.current;

        if (form) {
          form.submit();
          clearOrder();
        }
        return;
      }
      if (quote) {
        // Usual quote checkout
        try {
          const order = await checkoutQuote({ quote_id: quote.id }).unwrap();

          const shipping_address = addressList.find(
            (item) => item.id === order.shipping_address_id,
          );
          const billing_address = addressList.find(
            (item) => item.id === order.billing_address_id,
          );
          clearOrder();
          markOrderAsQuote();
          updateOrderShoppingCartId(order.cart_id);
          updateOrderShippingStepData({
            commited: true,
            data: {
              shipping_address_id: shipping_address.id,
            },
          });
          updateOrderBillingStepData({
            commited: true,
            data: {
              billing_address_id: billing_address.id,
            },
          });
          history.push('/checkout/payment');
        } catch (ex) {
          m.responseError(ex);
        }
        return;
      }
      // Usual checkout, simply clear redux order data before redirect
      clearOrder();
    },
    [onClick, greenwing, quote, checkoutQuote, addressList, history, m],
  );

  const ButtonWrapper = useMemo(() => {
    if (greenwing) {
      return Fragment;
    }
    if (!groupAdminApproveNeeded) {
      return Fragment;
    }
    // Checkout not allowed because approve needed
    return withProps(Tooltip, {
      arrow: true,
      interactive: true,
      variant: 'warning',
      placement: 'bottom',
      title: 'This order can only be placed by the administrator',
      children: ({ children }) => <div>{children}</div>,
    });
  }, [greenwing, groupAdminApproveNeeded]);

  const Button = useMemo(() => {
    return withProps(ButtonComponent, {
      ...restButtonProps,
      size,
      loading,
      fullWidth,
      children: buttonLabel,
      disabled: buttonDisabled,
      color: greenwing ? 'secondary' : 'primary',
      component: !greenwing && !quote ? Link : undefined,
      to: !greenwing && !quote ? '/checkout' : undefined,
      onClick: handleClick,
    });
  }, [
    ButtonComponent,
    buttonDisabled,
    buttonLabel,
    fullWidth,
    greenwing,
    handleClick,
    loading,
    restButtonProps,
    size,
    quote,
  ]);

  // Generate Greenwing form data if greenwing login used
  const formValue = useMemo(() => {
    if (!greenwingItems || !greenwing) {
      return null;
    }
    const result = {
      buyercookie: greenwing.buyer_cookie,
      body: {
        items: greenwingItems,
      },
    };
    return JSON.stringify(result, null, 2);
  }, [greenwing, greenwingItems]);

  return (
    <ButtonWrapper>
      <Button />

      {greenwing && (
        <form
          ref={formRef}
          method="POST"
          className={classes.form}
          action={greenwing.return_url}
        >
          <input name="cartdata" value={formValue} />
        </form>
      )}
    </ButtonWrapper>
  );
};

export default CheckoutButton;
